
import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { FileText, AlertCircle, ExternalLink } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Document, Page, pdfjs } from "react-pdf";

pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

const SUPABASE_URL = "https://sekiwtiutnikmdbpiufi.supabase.co";
const SUPABASE_ANON_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InNla2l3dGl1dG5pa21kYnBpdWZpIiwicm9sZSI6ImFub24iLCJpYXQiOjE3MzUzOTI0MzIsImV4cCI6MjA1MDk2ODQzMn0.OYCXJ_wPnVWdgSeTHGc_fHaHP5pHhsGBt1TN2zeLz0M";
interface CVTabProps {
  candidate: {
    full_name: string;
    email: string;
    cv_url?: string;
    [key: string]: any;
  };
}

export function CVTab({ candidate }: CVTabProps) {
  const [secureCVUrl, setSecureCVUrl] = useState<string | null>(null);
  const [authToken, setAuthToken] = useState<string | null>(null);
  const [numPages, setNumPages] = useState<number>(0);
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null);
  
  // Load secure CV URL when component mounts
  useEffect(() => {
    const load = async () => {
      if (!candidate.cv_url) return;

      // Get current session token
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token || null;
      setAuthToken(token);

      // Determine file path inside cvs bucket
      let filePath = candidate.cv_url;
      
      // Handle both public and private storage URLs
      if (candidate.cv_url.includes('/storage/v1/object/')) {
        const publicMatch = candidate.cv_url.match(/\/storage\/v1\/object\/public\/cvs\/(.+)$/);
        const privateMatch = candidate.cv_url.match(/\/storage\/v1\/object\/cvs\/(.+)$/);
        
        if (publicMatch) {
          filePath = publicMatch[1];
        } else if (privateMatch) {
          filePath = privateMatch[1];
        }
      } else if (candidate.cv_url.includes('/functions/v1/webcam-proxy')) {
        // If stored as proxy URL already, just use it
        setSecureCVUrl(candidate.cv_url);
        return;
      }

      const proxy = `${SUPABASE_URL}/functions/v1/webcam-proxy?file_path=${encodeURIComponent(filePath)}&bucket=cvs`;
      setSecureCVUrl(proxy);
    };

    load();
  }, [candidate.cv_url]);

  // Fetch PDF as blob with Authorization header for in-app preview
  useEffect(() => {
    const fetchPdf = async () => {
      if (!secureCVUrl || !authToken) return;
      if (!candidate.cv_url || getFileExtension(candidate.cv_url) !== 'pdf') return;
      try {
        const res = await fetch(secureCVUrl, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        if (!res.ok) throw new Error(`Preview fetch failed: ${res.status}`);
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        setPdfBlobUrl(url);
      } catch (e) {
        console.error('Failed to load PDF preview', e);
      }
    };

    fetchPdf();
    return () => {
      if (pdfBlobUrl) URL.revokeObjectURL(pdfBlobUrl);
    };
  }, [secureCVUrl, authToken, candidate.cv_url]);
  
  const handleViewCV = async () => {
    if (!secureCVUrl) return;
    try {
      // Prefer authorized fetch to support JWT-protected edge function
      if (authToken) {
        const res = await fetch(secureCVUrl, {
          headers: {
            Authorization: `Bearer ${authToken}`,
            apikey: SUPABASE_ANON_KEY,
          },
        });
        if (!res.ok) throw new Error(`Fetch failed: ${res.status}`);
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank');
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      } else {
        // Fallback
        window.open(secureCVUrl, '_blank');
      }
    } catch (e) {
      console.error('Open CV failed', e);
      window.open(secureCVUrl, '_blank');
    }
  };

  const getFileExtension = (url: string) => {
    try {
      const u = new URL(url);
      return u.pathname.split('.').pop()?.toLowerCase() || 'pdf';
    } catch {
      return url.split('?')[0].split('.').pop()?.toLowerCase() || 'pdf';
    }
  };

  const getFileTypeIcon = (url: string) => {
    const extension = getFileExtension(url);
    return <FileText className="h-16 w-16 text-muted-foreground" />;
  };

  if (!candidate.cv_url && !secureCVUrl) {
    return (
      <Card className="shadow-sm border">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Candidate CV
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <AlertCircle className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium text-muted-foreground mb-2">
              No CV Available
            </h3>
            <p className="text-sm text-muted-foreground max-w-md">
              {candidate.full_name} has not uploaded a CV yet. You can reach out to them directly at {candidate.email} to request their CV.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="shadow-sm border">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileText className="h-5 w-5" />
          Candidate CV
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* CV File Info */}
        <div className="flex items-center justify-between p-4 border rounded-lg bg-muted/20">
          <div className="flex items-center gap-4">
            {getFileTypeIcon(candidate.cv_url)}
            <div>
              <h4 className="font-medium">{candidate.full_name}_CV.{getFileExtension(candidate.cv_url)}</h4>
              <p className="text-sm text-muted-foreground">
                Curriculum Vitae • {getFileExtension(candidate.cv_url).toUpperCase()}
              </p>
            </div>
          </div>
          <Button onClick={handleViewCV} variant="outline" size="sm">
            <ExternalLink className="h-4 w-4 mr-2" />
            Open CV
          </Button>
        </div>

        {/* CV Viewer for PDFs */}
        {candidate.cv_url && getFileExtension(candidate.cv_url) === 'pdf' && (
          <div className="border rounded-lg overflow-hidden">
            <div className="bg-muted/30 p-3 border-b">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">CV Preview</span>
                <Button onClick={handleViewCV} variant="ghost" size="sm">
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Open in New Tab
                </Button>
              </div>
            </div>
            <div className="relative h-96 bg-white">
              {pdfBlobUrl ? (
                <div className="w-full h-full overflow-auto">
                  <Document
                    file={pdfBlobUrl}
                    onLoadSuccess={({ numPages }) => setNumPages(numPages)}
                    onLoadError={(err) => console.error('PDF load error', err)}
                  >
                    <Page pageNumber={1} width={800} renderTextLayer={false} renderAnnotationLayer={false} />
                  </Document>
                </div>
              ) : secureCVUrl && authToken ? (
                <div className="flex items-center justify-center h-full">
                  <p className="text-muted-foreground">Authorizing preview...</p>
                </div>
              ) : (
                <div className="flex items-center justify-center h-full">
                  <p className="text-muted-foreground">Loading CV preview...</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Non-PDF files */}
        {candidate.cv_url && getFileExtension(candidate.cv_url) !== 'pdf' && (
          <div className="text-center py-8">
            <FileText className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
            <p className="text-sm text-muted-foreground mb-4">
              Preview not available for {getFileExtension(candidate.cv_url).toUpperCase()} files
            </p>
            <Button onClick={handleViewCV}>
              <ExternalLink className="h-4 w-4 mr-2" />
              Open CV
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
